---
title: The data folder
lede: Everything Airlock keeps on your computer, and how to move it.
order: 52
part: Help
---

Airlock keeps its database, sandboxes, caches and logs in one folder: `~/.airlock`. To open it, use **Reveal data folder** in the File or Debug menu, or **Settings › General › Application data › Reveal folder**.

### What is in it

| Folder | What it holds |
|---|---|
| `sandboxes/` | One directory per session: its copy of your repositories, under `workspace/` — a git worktree of each when the session runs on this computer |
| `bundles/` | Each repository packed for its trip into a VM |
| `logs/` | Project output from the sandboxes |
| `transcripts/` | What the agents said |
| `maps/` | Each session's [Change Map](/change-map) |
| `rules/` | Each session's last [Rules](/rules) audit |
| `shots/` | Screenshots the agent took in [Preview](/agent-preview) |
| `host-edits/` | A session's files opened in an app on this computer |
| `attachments/` | Files from imported tickets, one directory per task |
| `runs/` | What each [workflow](/workflows) run's steps produced, one file per step |
| `rules-repos/` | The Rules repositories as this computer fetched them, shared by a workspace's sessions |
| `deps/` | Installed dependency trees, shared by every workspace |
| `microsandbox/` | The micro-VM runtime: its image cache, session-image snapshots and warm images |
| `bin/` | The tools Airlock manages, such as the GitHub CLI and the tunnel agent |
| `gh/`, `ngrok/` | Those tools' own sign-in and config |

At the root are `airlock.db` (sessions, workspaces, tasks, workflows), `warm-images.json`, `base-images.json`, `privacy-mask.key`, `updater.log`, `airlock.pid` and `cli.sock`, the socket the [command line](/command-line) talks to.

Deleting a session removes its directories. One left behind by a session that no longer exists is an orphan; **Sweep orphans** in [Diagnostics](/diagnostics) removes it. The caches and tools belong to the whole install and are never swept.

Tens of gigabytes is normal with a few sessions. **Diagnostics › Data folder** and **Caches** show where the space goes.

### Moving it

Set `AIRLOCK_HOME` before starting Airlock. Everything follows it, including the micro-VM runtime.

```bash
AIRLOCK_HOME=/Volumes/fast/airlock /Applications/Airlock.app/Contents/MacOS/Airlock
```

Keep the path short. macOS refuses socket paths over 104 bytes, and a deeply nested folder stops every micro-VM from starting.

Only one Airlock can use a data folder. A second one says *Airlock is already running* and quits; give it its own `AIRLOCK_HOME`.

### Forcing the local backend

`AIRLOCK_SANDBOX=local` turns off micro-VMs, so every session runs on this computer. Use it only when the runtime will not load.

:::warn No isolation
On the local backend there is no hypervisor and no network policy. The agent runs with your user's rights, on your kernel, with your network.
:::

**Diagnostics › Environment** shows both variables. See [Where a session runs](/placement) to choose per session instead.

### What is not in it

**Your repositories.** Airlock reads them, and writes to them only when you [merge](/merging). A session on this computer also registers a `git worktree` and a `session/<id>` branch, removed with the session.

**Your credentials.** The subscription token, tunnel authtoken, licence key, vault passwords and secret variables are sealed with the macOS Keychain.
